import { useEffect } from 'react';
import type { TableSelectedCell } from '@/components/custom/erp/input-table-v2';
import { useTableRows } from '@/components/custom/erp/input-table-v2';

export interface DetailRow {
  uuid?: string | null;
  gia_nt?: number;
  tien_nt?: number;
  tien_nt_goc?: number;
  thue_nt?: number;
  so_luong?: number;
  ma_thue_data?: {
    thue_suat?: number;
    [key: string]: MuaHangRecord;
  };
  [key: string]: MuaHangRecord;
}

export type SelectedDetailCellInfo = TableSelectedCell;

export function useDetailRows(
  initialRows: DetailRow[] = [],
  loai_ck: string = '1',
  ck_tl_nt: number = 0,
  ck_yn: boolean = false
) {
  const handleFieldValueChange = (row: DetailRow, field: string, value: MuaHangRecord): Partial<DetailRow> => {
    const extraFields: Partial<DetailRow> = {};

    if (field === 'gia_nt') {
      const so_luong = Number(row.so_luong) || 1;
      const gia_nt = Number(value) || 0;
      const tien_tck_nt = gia_nt * so_luong;
      const tl_ck = Number(row.tl_ck) || 0;
      const ck_nt = Math.round(((tien_tck_nt * tl_ck) / 100) * 10) / 10;
      const tien_nt = Math.round((tien_tck_nt - ck_nt) * 10) / 10;

      const taxRate = row.ma_thue_data?.thue_suat || 0;
      const calculatedTax = Math.round((tien_nt * taxRate) / 100);

      extraFields.gia_nt = gia_nt;
      extraFields.tien_nt = tien_nt;
      extraFields.tien_tck_nt = tien_tck_nt;
      extraFields.ck_nt = ck_nt;
      extraFields.thue_nt = calculatedTax;
    } else if (field === 'so_luong') {
      const gia_nt = Number(row.gia_nt || row.ma_dv_data?.gia_nt) || 0;
      const so_luong = Number(value) || 1;
      const tien_tck_nt = gia_nt * so_luong;
      const tl_ck = Number(row.tl_ck) || 0;
      const ck_nt = Math.round(((tien_tck_nt * tl_ck) / 100) * 10) / 10;
      const tien_nt = Math.round((tien_tck_nt - ck_nt) * 10) / 10;

      const taxRate = row.ma_thue_data?.thue_suat || 0;
      const calculatedTax = Math.round((tien_nt * taxRate) / 100);

      extraFields.gia_nt = gia_nt;
      extraFields.tien_nt = tien_nt;
      extraFields.tien_tck_nt = tien_tck_nt;
      extraFields.ck_nt = ck_nt;
      extraFields.thue_nt = calculatedTax;
    }

    const shouldCalculateTax = ['tien_nt', 'gia_nt', 'ma_thue_data'].includes(field);
    if (shouldCalculateTax) {
      const baseAmount =
        field === 'tien_nt'
          ? Number(value)
          : field === 'gia_nt'
            ? (Number(value) || 0) * (Number(row.so_luong) || 0)
            : row.tien_nt || (row.gia_nt || 0) * (row.so_luong || 0) || 0;

      const taxRate = field === 'ma_thue_data' ? value?.thue_suat || 0 : row.ma_thue_data?.thue_suat || 0;

      extraFields.thue_nt = Math.round((baseAmount * taxRate) / 100);
    }

    if (field === 'tl_ck' && loai_ck === '1') {
      const tien_tck_nt = Number(row.tien_tck_nt) || 0;
      const tl_ck = Number(value) || 0;
      const ck_nt = Math.round(((tien_tck_nt * tl_ck) / 100) * 10) / 10;
      const tien_nt = Math.round((tien_tck_nt - ck_nt) * 10) / 10;

      const taxRate = row.ma_thue_data?.thue_suat || 0;
      const calculatedTax = Math.round((tien_nt * taxRate) / 100);

      extraFields.ck_nt = ck_nt;
      extraFields.tien_nt = tien_nt;
      extraFields.tien_tck_nt = tien_tck_nt;
      extraFields.thue_nt = calculatedTax;
    } else if (field === 'ck_nt' && loai_ck === '1') {
      const tien_tck_nt = Number(row.tien_tck_nt) || 0;
      const ck_nt = Number(value) || 0;
      const tien_nt = Math.round((tien_tck_nt - ck_nt) * 10) / 10;
      const tl_ck = tien_tck_nt > 0 ? Math.round((ck_nt / tien_tck_nt) * 100 * 10) / 10 : 0;

      const taxRate = row.ma_thue_data?.thue_suat || 0;
      const calculatedTax = Math.round((tien_nt * taxRate) / 100);

      extraFields.ck_nt = ck_nt;
      extraFields.tien_nt = tien_nt;
      extraFields.tien_tck_nt = tien_tck_nt;
      extraFields.tl_ck = tl_ck;
      extraFields.thue_nt = calculatedTax;
    } else if (field === 'tien_tck_nt') {
      const tien_tck_nt = Number(value) || 0;
      const tl_ck = Number(row.tl_ck) || 0;
      const ck_nt = Math.round(((tien_tck_nt * tl_ck) / 100) * 10) / 10;
      const tien_nt = Math.round((tien_tck_nt - ck_nt) * 10) / 10;

      const taxRate = row.ma_thue_data?.thue_suat || 0;
      const calculatedTax = Math.round((tien_nt * taxRate) / 100);

      extraFields.ck_nt = ck_nt;
      extraFields.tien_nt = tien_nt;
      extraFields.tien_tck_nt = tien_tck_nt;
      extraFields.thue_nt = calculatedTax;
    }

    return extraFields;
  };

  const defaultRow = { uuid: 'temp-1' } as DetailRow;
  const processedInitialRows = initialRows && initialRows.length > 0 ? initialRows : [defaultRow];

  const {
    rows,
    setRows,
    selectedRowUuid,
    setSelectedRowUuid,
    selectedRow,
    setSelectedRow,
    selectedCell,
    setSelectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    setNonWritableFields,
    setPasteConfigs,
    isPasting
  } = useTableRows<MuaHangRecord>({
    initialRows: processedInitialRows,
    defaultRows: 1,
    onFieldValueChange: handleFieldValueChange,
    defaultField: 'ma_dv'
  });

  useEffect(() => {
    if (loai_ck && ck_tl_nt !== undefined && ck_tl_nt !== null && ck_yn) {
      setRows(currentRows => {
        let hasChanged = false;
        const nextRows = currentRows.map(row => {
          const tien_tck_nt = Number(row.gia_nt || 0) * Number(row.so_luong || 0) || 0;
          let tl_ck = Number(row.tl_ck) || 0;
          let ck_nt = Number(row.ck_nt) || 0;

          if (loai_ck === '2') {
            tl_ck = ck_tl_nt;
            ck_nt = Math.round(((tien_tck_nt * tl_ck) / 100) * 10) / 10;
          } else if (loai_ck === '3') {
            ck_nt = ck_tl_nt;
            tl_ck = tien_tck_nt > 0 ? Math.round((ck_nt / tien_tck_nt) * 100 * 10) / 10 : 0;
          }

          const tien_nt = Math.round((tien_tck_nt - ck_nt) * 10) / 10;

          const taxRate = row.ma_thue_data?.thue_suat || 0;
          const calculatedTax = Math.round((tien_nt * taxRate) / 100);

          if (
            row.tien_nt === tien_nt &&
            row.tien_tck_nt === tien_tck_nt &&
            row.ck_nt === ck_nt &&
            row.tl_ck === tl_ck &&
            row.thue_nt === calculatedTax
          ) {
            return row;
          }

          hasChanged = true;

          return {
            ...row,
            tien_nt: tien_nt,
            tien_tck_nt: tien_tck_nt,
            ck_nt: ck_nt,
            tl_ck: tl_ck,
            thue_nt: calculatedTax
          };
        });

        return hasChanged ? nextRows : currentRows;
      });
    }
  }, [loai_ck, ck_tl_nt, setRows, ck_yn]);

  return {
    rows,
    setRows,
    selectedRowUuid,
    setSelectedRowUuid,
    selectedRow,
    setSelectedRow,
    selectedCell,
    setSelectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    setNonWritableFields,
    setPasteConfigs,
    isPasting
  };
}
